import { useMemo, useState } from "react";
import {
  Button,
  Card,
  Grid,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { AnalyticsSummary } from "../api/schemas";
import { EmptyState, ErrorState, LoadingState } from "../components/ui";
import { useMarket } from "../hooks/useMarket";
import { monoStyle } from "../theme";

function todayMidnightInput(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T00:00`;
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function toIsoOrUndef(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d.toISOString() : undefined;
}

/**
 * Shared body for the per-market dashboard (/analytics/current) and the
 * aggregate cross-market dashboard (/analytics/all). When `marketId` is
 * `null` the API query omits `market_id`, which the data-api interprets
 * as "aggregate across every market in the catalogue".
 */
export function AnalyticsDashboardBody({
  marketId,
  titleSuffix,
}: {
  marketId: string | null;
  titleSuffix: string;
}) {
  const [mode, setMode] = useState<"live" | "paper" | "all">("live");
  // Default the aggregate "All Markets" view to today (from midnight) so the
  // initial load doesn't crunch the entire (very large) dataset. Clearing the
  // "From" field falls back to the full dataset. The per-market view keeps its
  // original "no filter" default.
  const [from, setFrom] = useState(marketId === null ? todayMidnightInput() : "");
  const [to, setTo] = useState("");
  // All Markets only: which asset is the "primary" for the counterpart card.
  const [cpPrimary, setCpPrimary] = useState<"btc" | "eth">("btc");

  const q = useQuery({
    // `marketId ?? "__all__"` keeps the cache key stable for the
    // aggregate view without colliding with any real market id.
    queryKey: [
      "stats",
      "analytics",
      { marketId: marketId ?? "__all__", mode, from, to, cpPrimary: marketId === null ? cpPrimary : null },
    ],
    queryFn: () => api.stats.analytics({
      mode,
      from: toIsoOrUndef(from),
      to: toIsoOrUndef(to),
      // Omit market_id entirely on the aggregate route so the data-api
      // doesn't add a marketWindows.market_id WHERE clause.
      ...(marketId ? { market_id: marketId } : { cp_primary: cpPrimary }),
    }),
    staleTime: 60_000,
  });

  const s = q.data;
  const winRatePct = s ? (s.running.win_rate * 100) : 0;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="baseline" wrap="wrap" gap="md">
        <Title order={2}>Analytics — {titleSuffix}</Title>
        <Group gap="md" wrap="wrap">
          <SegmentedControl
            value={mode}
            onChange={(v) => setMode(v as typeof mode)}
            data={[
              { label: "Live", value: "live" },
              { label: "Paper", value: "paper" },
              { label: "All", value: "all" },
            ]}
            size="md"
          />
          <TextInput
            size="md"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.currentTarget.value)}
            placeholder="From"
            w={270}
          />
          <TextInput
            size="md"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            placeholder="To"
            w={270}
          />
          <Button size="md" variant="default" onClick={() => { setFrom(""); setTo(""); }}>
            Reset
          </Button>
          <Button
            size="md"
            variant="light"
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getTime() - 24 * 3600 * 1000);
              setFrom(toLocalInput(start.toISOString()));
              setTo(toLocalInput(now.toISOString()));
            }}
          >
            Last 24h
          </Button>
        </Group>
      </Group>

      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error as Error} onRetry={() => q.refetch()} /> : null}
      {!q.isLoading && !q.isError && !s ? <EmptyState label="No analytics data" /> : null}

      {s ? (
        <>
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Running Stats ({mode})</Title>
              <Grid>
                <Grid.Col span={{ base: 6, md: 2 }}><Stat label="Trades" value={String(s.running.trades)} /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 2 }}><Stat label="Wins" value={String(s.running.wins)} color="var(--mantine-color-profit-5)" /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 2 }}><Stat label="Losses" value={String(s.running.losses)} color="var(--mantine-color-loss-5)" /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 2 }}><Stat label="Win Rate" value={`${winRatePct.toFixed(1)}%`} /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 2 }}><Stat label="Win Streak" value={String(s.running.current_win_streak)} /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 2 }}><Stat label="Avg wins before loss" value={s.running.avg_wins_before_loss.toFixed(2)} /></Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={{ base: 12, md: 4 }}><Stat label="Total Won" value={`+$${s.running.total_won_usdc.toFixed(2)}`} color="var(--mantine-color-profit-5)" /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}><Stat label="Total Lost" value={`-$${s.running.total_lost_usdc.toFixed(2)}`} color="var(--mantine-color-loss-5)" /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}><Stat label="Net P/L" value={`${s.running.net_pnl_usdc >= 0 ? "+" : ""}$${s.running.net_pnl_usdc.toFixed(2)}`} color={s.running.net_pnl_usdc >= 0 ? "var(--mantine-color-profit-5)" : "var(--mantine-color-loss-5)"} /></Grid.Col>
              </Grid>
            </Stack>
          </Card>

          {marketId === null ? <ByMarketCounterpartCard byMarket={s.by_market} /> : null}

          <CounterpartRelationshipCard
            data={s.counterpart}
            primary={cpPrimary}
            onPrimaryChange={marketId === null ? setCpPrimary : undefined}
          />

          <Card withBorder>
            <Stack gap="md">
              <Group justify="space-between" align="baseline">
                <Title order={4}>Kronos Gate ({mode})</Title>
                <Text size="xs" c="dimmed">
                  Wins/losses on trades whose governing config had the Kronos forecast gate enabled
                </Text>
              </Group>
              <Grid>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Stat label="Total Wins" value={String(s.kronos.enabled_wins)} color="var(--mantine-color-profit-5)" />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Stat label="Total Losses" value={String(s.kronos.enabled_losses)} color="var(--mantine-color-loss-5)" />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Stat label="Win Rate" value={`${(s.kronos.enabled_win_rate * 100).toFixed(1)}%`} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Stat
                    label="Net P/L"
                    value={`${s.kronos.enabled_net_pnl_usdc >= 0 ? "+" : ""}$${s.kronos.enabled_net_pnl_usdc.toFixed(2)}`}
                    color={s.kronos.enabled_net_pnl_usdc >= 0 ? "var(--mantine-color-profit-5)" : "var(--mantine-color-loss-5)"}
                  />
                </Grid.Col>
              </Grid>

              <Text size="sm" fw={600} mt="xs">By market</Text>
              {s.kronos.by_market.length === 0 ? (
                <Text size="sm" c="dimmed">No Kronos-enabled trades in this range.</Text>
              ) : (
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Market</Table.Th>
                      <Table.Th ta="right">Wins</Table.Th>
                      <Table.Th ta="right">Losses</Table.Th>
                      <Table.Th ta="right">Win Rate</Table.Th>
                      <Table.Th ta="right">Net P/L</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {s.kronos.by_market.map((m) => {
                      const total = m.wins + m.losses;
                      const wr = total ? (m.wins / total) * 100 : 0;
                      return (
                        <Table.Tr key={m.market_id}>
                          <Table.Td style={monoStyle}>{m.market_id}</Table.Td>
                          <Table.Td ta="right" style={monoStyle} c="var(--mantine-color-profit-5)">{m.wins}</Table.Td>
                          <Table.Td ta="right" style={monoStyle} c="var(--mantine-color-loss-5)">{m.losses}</Table.Td>
                          <Table.Td ta="right" style={monoStyle}>{wr.toFixed(1)}%</Table.Td>
                          <Table.Td
                            ta="right"
                            style={monoStyle}
                            c={m.net_pnl_usdc >= 0 ? "var(--mantine-color-profit-5)" : "var(--mantine-color-loss-5)"}
                          >
                            {`${m.net_pnl_usdc >= 0 ? "+" : ""}$${m.net_pnl_usdc.toFixed(2)}`}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>Directional Breakdown ({mode})</Title>
              <Grid>
                <Grid.Col span={{ base: 6, md: 3 }}><Stat label="Avg UP fill price" value={s.directional.avg_up_fill_price.toFixed(3)} color="var(--mantine-color-profit-5)" /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}><Stat label="Avg DOWN fill price" value={s.directional.avg_down_fill_price.toFixed(3)} color="var(--mantine-color-loss-5)" /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}><Stat label="UP — Wins | Losses" value={`${s.directional.up_wins} | ${s.directional.up_losses}`} /></Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}><Stat label="DOWN — Wins | Losses" value={`${s.directional.down_wins} | ${s.directional.down_losses}`} /></Grid.Col>
              </Grid>
            </Stack>
          </Card>

          <PnlBreakdownCard series={s.pnl_series} from={from} to={to} />
        </>
      ) : null}
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// P&L breakdown — day-by-day (calendar grid) + full-week (Sunday–Saturday)
// totals for the selected range.
// -----------------------------------------------------------------------------

type PnlPoint = AnalyticsSummary["pnl_series"][number];

interface DayCell {
  /** Local date key, YYYY-MM-DD. */
  key: string;
  date: Date;
  /** True when the day falls inside the selected range. */
  inRange: boolean;
  net: number;
  trades: number;
  wins: number;
  losses: number;
}

interface WeekRow {
  /** Sunday that starts the week (local midnight). */
  weekStart: Date;
  days: DayCell[];
  /** True when all 7 days lie within the selected range. */
  full: boolean;
  net: number;
  trades: number;
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MS_PER_DAY = 24 * 3600 * 1000;

function dayKey(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Sunday (local midnight) of the week containing `d`. */
function startOfWeekSunday(d: Date): Date {
  const s = startOfLocalDay(d);
  s.setDate(s.getDate() - s.getDay());
  return s;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmtUsd(n: number): string {
  const sign = n >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

function fmtMonthDay(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Bucket a per-trade P&L series into a Sunday–Saturday calendar. The visible
 * span is driven by the operator's `from`/`to` datetime-local filter when set,
 * otherwise by the min/max trade timestamp in the data. Day boundaries use the
 * operator's local timezone so they line up with the filter inputs.
 */
function buildCalendar(
  series: PnlPoint[],
  from: string,
  to: string,
): { weeks: WeekRow[]; spanDays: number; totalNet: number; totalTrades: number } {
  const byDay = new Map<string, { net: number; trades: number; wins: number; losses: number }>();
  let minTs = Infinity;
  let maxTs = -Infinity;
  for (const p of series) {
    const d = new Date(p.ts);
    const t = d.getTime();
    if (!Number.isFinite(t)) continue;
    minTs = Math.min(minTs, t);
    maxTs = Math.max(maxTs, t);
    const key = dayKey(d);
    const agg = byDay.get(key) ?? { net: 0, trades: 0, wins: 0, losses: 0 };
    agg.net += p.net_pnl_usdc;
    agg.trades += 1;
    if (p.net_pnl_usdc > 0) agg.wins += 1;
    else agg.losses += 1;
    byDay.set(key, agg);
  }

  const fromDate = from ? new Date(from) : null;
  const toDate = to ? new Date(to) : null;
  const hasData = Number.isFinite(minTs) && Number.isFinite(maxTs);

  const rangeStart = fromDate && Number.isFinite(fromDate.getTime())
    ? startOfLocalDay(fromDate)
    : hasData ? startOfLocalDay(new Date(minTs)) : null;
  const rangeEnd = toDate && Number.isFinite(toDate.getTime())
    ? startOfLocalDay(toDate)
    : hasData ? startOfLocalDay(new Date(maxTs)) : null;

  if (!rangeStart || !rangeEnd || rangeEnd.getTime() < rangeStart.getTime()) {
    return { weeks: [], spanDays: 0, totalNet: 0, totalTrades: 0 };
  }

  const spanDays = Math.round((rangeEnd.getTime() - rangeStart.getTime()) / MS_PER_DAY) + 1;

  const gridStart = startOfWeekSunday(rangeStart);
  const gridEnd = addDays(startOfWeekSunday(rangeEnd), 6);
  const weekCount = Math.round((gridEnd.getTime() - gridStart.getTime()) / MS_PER_DAY + 1) / 7;

  const weeks: WeekRow[] = [];
  let totalNet = 0;
  let totalTrades = 0;
  for (let w = 0; w < weekCount; w += 1) {
    const weekStart = addDays(gridStart, w * 7);
    const days: DayCell[] = [];
    let weekNet = 0;
    let weekTrades = 0;
    let inRangeCount = 0;
    for (let i = 0; i < 7; i += 1) {
      const date = addDays(weekStart, i);
      const inRange = date.getTime() >= rangeStart.getTime() && date.getTime() <= rangeEnd.getTime();
      const agg = inRange ? byDay.get(dayKey(date)) : undefined;
      const net = agg?.net ?? 0;
      const trades = agg?.trades ?? 0;
      if (inRange) {
        inRangeCount += 1;
        weekNet += net;
        weekTrades += trades;
        totalNet += net;
        totalTrades += trades;
      }
      days.push({
        key: dayKey(date),
        date,
        inRange,
        net,
        trades,
        wins: agg?.wins ?? 0,
        losses: agg?.losses ?? 0,
      });
    }
    weeks.push({ weekStart, days, full: inRangeCount === 7, net: weekNet, trades: weekTrades });
  }

  return { weeks, spanDays, totalNet, totalTrades };
}

function pnlColor(net: number): string {
  return net >= 0 ? "var(--mantine-color-profit-5)" : "var(--mantine-color-loss-5)";
}

function DayCellBox({ cell }: { cell: DayCell }) {
  if (!cell.inRange) {
    return <div style={{ flex: 1, minWidth: 0 }} aria-hidden />;
  }
  const hasTrades = cell.trades > 0;
  return (
    <Paper
      withBorder
      radius="md"
      p={6}
      style={{
        flex: 1,
        minWidth: 0,
        backgroundColor: hasTrades
          ? (cell.net >= 0
              ? "color-mix(in srgb, var(--mantine-color-profit-5) 14%, transparent)"
              : "color-mix(in srgb, var(--mantine-color-loss-5) 14%, transparent)")
          : undefined,
      }}
    >
      <Stack gap={2}>
        <Group justify="space-between" gap={2} wrap="nowrap">
          <Text size="xs" c="dimmed" style={monoStyle}>{cell.date.getDate()}</Text>
          {hasTrades ? <Text size="9px" c="dimmed" style={monoStyle}>{cell.trades}t</Text> : null}
        </Group>
        <Text
          size="xs"
          fw={700}
          style={{ ...monoStyle, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          c={hasTrades ? pnlColor(cell.net) : "dimmed"}
        >
          {hasTrades ? fmtUsd(cell.net) : "—"}
        </Text>
      </Stack>
    </Paper>
  );
}

/** Phone layout: one line per day instead of a 7-wide calendar grid. */
function DayRow({ cell }: { cell: DayCell }) {
  const hasTrades = cell.trades > 0;
  return (
    <Group justify="space-between" wrap="nowrap" px={4}>
      <Text size="xs" c="dimmed" style={monoStyle}>
        {WEEKDAY_LABELS[cell.date.getDay()]} {cell.date.getDate()}
      </Text>
      <Group gap="sm" wrap="nowrap">
        {hasTrades ? <Text size="xs" c="dimmed" style={monoStyle}>{cell.trades}t</Text> : null}
        <Text size="xs" fw={700} style={monoStyle} c={hasTrades ? pnlColor(cell.net) : "dimmed"}>
          {hasTrades ? fmtUsd(cell.net) : "—"}
        </Text>
      </Group>
    </Group>
  );
}

function PnlBreakdownCard({ series, from, to }: { series: PnlPoint[]; from: string; to: string }) {
  const { weeks, spanDays, totalNet, totalTrades } = useMemo(
    () => buildCalendar(series, from, to),
    [series, from, to],
  );
  const isMobile = useMediaQuery("(max-width: 48em)", false, { getInitialValueInEffect: false });

  const showWeekTotals = spanDays > 7;

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="baseline" wrap="wrap" gap="xs">
          <Title order={4}>P&L Breakdown</Title>
          <Text size="sm" c="dimmed">
            {spanDays > 0 ? `${spanDays} day${spanDays === 1 ? "" : "s"} · ${totalTrades} trades · ` : ""}
            {totalTrades > 0 ? (
              <Text span fw={700} c={pnlColor(totalNet)} style={monoStyle}>{fmtUsd(totalNet)}</Text>
            ) : null}
          </Text>
        </Group>

        {weeks.length === 0 ? (
          <EmptyState label="No trades in this range" />
        ) : isMobile ? (
          <Stack gap="md">
            {weeks.map((week) => (
              <Stack key={week.weekStart.toISOString()} gap={4}>
                <Group justify="space-between" wrap="nowrap" px={4}>
                  <Text size="xs" c="dimmed" fw={600}>Week of {fmtMonthDay(week.weekStart)}</Text>
                  {showWeekTotals ? (
                    <Text size="sm" fw={800} style={monoStyle} c={pnlColor(week.net)}>
                      {fmtUsd(week.net)}
                      {week.full ? null : <Text span size="xs" c="dimmed" ml={4}>*</Text>}
                    </Text>
                  ) : null}
                </Group>
                <Paper withBorder radius="md" py={4}>
                  <Stack gap={2}>
                    {week.days.filter((cell) => cell.inRange).map((cell) => (
                      <DayRow key={cell.key} cell={cell} />
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack gap="xs">
            {/* Weekday header */}
            <Group gap="xs" wrap="nowrap" px={4}>
              <Text w={92} size="xs" c="dimmed" fw={600} style={{ flexShrink: 0 }}>Week</Text>
              {WEEKDAY_LABELS.map((d) => (
                <Text key={d} size="xs" c="dimmed" fw={600} ta="center" style={{ flex: 1 }}>{d}</Text>
              ))}
              {showWeekTotals ? (
                <Text w={96} size="xs" c="dimmed" fw={600} ta="right" style={{ flexShrink: 0 }}>Week P&L</Text>
              ) : null}
            </Group>

            {weeks.map((week) => (
              <Group key={week.weekStart.toISOString()} gap="xs" wrap="nowrap" align="stretch">
                <Text w={92} size="xs" c="dimmed" style={{ ...monoStyle, flexShrink: 0, alignSelf: "center" }}>
                  {fmtMonthDay(week.weekStart)}
                </Text>
                {week.days.map((cell) => (
                  <DayCellBox key={cell.key} cell={cell} />
                ))}
                {showWeekTotals ? (
                  <div style={{ width: 96, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    <Text
                      size="sm"
                      fw={800}
                      ta="right"
                      style={monoStyle}
                      c={pnlColor(week.net)}
                      title={week.full ? undefined : "Week in progress — running total so far"}
                    >
                      {fmtUsd(week.net)}
                      {week.full ? null : <Text span size="xs" c="dimmed" ml={4}>*</Text>}
                    </Text>
                  </div>
                ) : null}
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// By Market & Counterpart — per-market trading stats with BTC and its ETH
// counterpart (same timeframe) shown side by side, one row per timeframe.
// -----------------------------------------------------------------------------

type MarketRow = AnalyticsSummary["by_market"][number];

const TF_ORDER = ["5m", "15m", "1h", "4h"];

function splitMarketId(id: string): { asset: string; timeframe: string } {
  const dash = id.indexOf("-");
  if (dash <= 0) return { asset: id, timeframe: "" };
  return { asset: id.slice(0, dash), timeframe: id.slice(dash + 1) };
}

function MarketStatCells({ m }: { m: MarketRow | undefined }) {
  if (!m) {
    return (
      <>
        <Table.Td ta="right" c="dimmed">—</Table.Td>
        <Table.Td ta="right" c="dimmed">—</Table.Td>
        <Table.Td ta="right" c="dimmed">—</Table.Td>
        <Table.Td ta="right" c="dimmed">—</Table.Td>
      </>
    );
  }
  return (
    <>
      <Table.Td ta="right" style={monoStyle}>{m.trades}</Table.Td>
      <Table.Td ta="right" style={monoStyle}>
        <Text span c="var(--mantine-color-profit-5)">{m.wins}</Text>
        <Text span c="dimmed">-</Text>
        <Text span c="var(--mantine-color-loss-5)">{m.losses}</Text>
      </Table.Td>
      <Table.Td ta="right" style={monoStyle}>{(m.win_rate * 100).toFixed(1)}%</Table.Td>
      <Table.Td ta="right" style={monoStyle} c={pnlColor(m.net_pnl_usdc)}>{fmtUsd(m.net_pnl_usdc)}</Table.Td>
    </>
  );
}

function ByMarketCounterpartCard({ byMarket }: { byMarket: MarketRow[] }) {
  const rows = useMemo(() => {
    const groups = new Map<string, { btc?: MarketRow; eth?: MarketRow }>();
    for (const m of byMarket) {
      const { asset, timeframe } = splitMarketId(m.market_id);
      const g = groups.get(timeframe) ?? {};
      if (asset === "btc") g.btc = m;
      else if (asset === "eth") g.eth = m;
      groups.set(timeframe, g);
    }
    return [...groups.entries()]
      .map(([timeframe, g]) => ({ timeframe, btc: g.btc, eth: g.eth }))
      .filter((r) => r.btc || r.eth)
      .sort((a, b) => {
        const ai = TF_ORDER.indexOf(a.timeframe);
        const bi = TF_ORDER.indexOf(b.timeframe);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.timeframe.localeCompare(b.timeframe);
      });
  }, [byMarket]);

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={4}>By Market &amp; Counterpart</Title>
          <Text size="xs" c="dimmed">Each timeframe's BTC market alongside its ETH counterpart</Text>
        </Group>
        {rows.length === 0 ? (
          <Text size="sm" c="dimmed">No trades in this range.</Text>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th rowSpan={2}>Timeframe</Table.Th>
                <Table.Th colSpan={4} ta="center" style={{ borderLeft: "1px solid var(--mantine-color-default-border)" }}>BTC</Table.Th>
                <Table.Th colSpan={4} ta="center" style={{ borderLeft: "1px solid var(--mantine-color-default-border)" }}>ETH (counterpart)</Table.Th>
              </Table.Tr>
              <Table.Tr>
                <Table.Th ta="right" style={{ borderLeft: "1px solid var(--mantine-color-default-border)" }}>Trades</Table.Th>
                <Table.Th ta="right">W-L</Table.Th>
                <Table.Th ta="right">Win%</Table.Th>
                <Table.Th ta="right">Net P/L</Table.Th>
                <Table.Th ta="right" style={{ borderLeft: "1px solid var(--mantine-color-default-border)" }}>Trades</Table.Th>
                <Table.Th ta="right">W-L</Table.Th>
                <Table.Th ta="right">Win%</Table.Th>
                <Table.Th ta="right">Net P/L</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.timeframe}>
                  <Table.Td style={monoStyle} fw={700}>{r.timeframe}</Table.Td>
                  <MarketStatCells m={r.btc} />
                  <MarketStatCells m={r.eth} />
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Counterpart Relationship — from the primary asset's trades, how the ETH/BTC
// counterpart behaved: (a) when the counterpart was NOT traded, which way it
// closed vs the primary trade; (b) when BOTH were traded, direction match and
// each side's win/loss. Win = pnl > 0.
// -----------------------------------------------------------------------------

type CounterpartData = AnalyticsSummary["counterpart"];
type NtBucket = { won: number; lost: number };
type NtAgg = { same: NtBucket; opposite: NtBucket; unresolved: NtBucket };
type BtBucket = { bw: number; pwcl: number; plcw: number; bl: number };
type BtAgg = { same: BtBucket; different: BtBucket };

function newNtAgg(): NtAgg {
  return { same: { won: 0, lost: 0 }, opposite: { won: 0, lost: 0 }, unresolved: { won: 0, lost: 0 } };
}
function newBtAgg(): BtAgg {
  return { same: { bw: 0, pwcl: 0, plcw: 0, bl: 0 }, different: { bw: 0, pwcl: 0, plcw: 0, bl: 0 } };
}

function orderedTimeframes(tfs: Iterable<string>): string[] {
  return [...new Set(tfs)].sort((a, b) => {
    const ai = TF_ORDER.indexOf(a);
    const bi = TF_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.localeCompare(b);
  });
}

/** Green won / red lost pair, e.g. "12 / 3". */
function WL({ won, lost }: NtBucket) {
  if (won + lost === 0) return <Text span c="dimmed">—</Text>;
  return (
    <>
      <Text span c="var(--mantine-color-profit-5)">{won}</Text>
      <Text span c="dimmed"> / </Text>
      <Text span c="var(--mantine-color-loss-5)">{lost}</Text>
    </>
  );
}

function CounterpartRelationshipCard({ data, primary, onPrimaryChange }: {
  data: CounterpartData;
  primary?: "btc" | "eth";
  onPrimaryChange?: (v: "btc" | "eth") => void;
}) {
  const P = data.primary_asset;
  const C = data.counterpart_asset;

  const nt = useMemo(() => {
    const byTf = new Map<string, NtAgg>();
    const all = newNtAgg();
    for (const r of data.not_traded) {
      const g = byTf.get(r.timeframe) ?? newNtAgg();
      g[r.counterpart_close].won += r.won;
      g[r.counterpart_close].lost += r.lost;
      byTf.set(r.timeframe, g);
      all[r.counterpart_close].won += r.won;
      all[r.counterpart_close].lost += r.lost;
    }
    return { byTf, all, tfs: orderedTimeframes(byTf.keys()) };
  }, [data.not_traded]);

  const bt = useMemo(() => {
    const byTf = new Map<string, BtAgg>();
    const all = newBtAgg();
    for (const r of data.both_traded) {
      const g = byTf.get(r.timeframe) ?? newBtAgg();
      const b = g[r.direction];
      b.bw += r.both_win; b.pwcl += r.primary_win_cp_loss; b.plcw += r.primary_loss_cp_win; b.bl += r.both_loss;
      byTf.set(r.timeframe, g);
      const a = all[r.direction];
      a.bw += r.both_win; a.pwcl += r.primary_win_cp_loss; a.plcw += r.primary_loss_cp_win; a.bl += r.both_loss;
    }
    return { byTf, all, tfs: orderedTimeframes(byTf.keys()) };
  }, [data.both_traded]);

  const ntTotal = (a: NtAgg) => a.same.won + a.same.lost + a.opposite.won + a.opposite.lost + a.unresolved.won + a.unresolved.lost;
  const btTotal = (b: BtBucket) => b.bw + b.pwcl + b.plcw + b.bl;

  const hasNt = nt.tfs.length > 0;
  const hasBt = bt.tfs.length > 0;

  return (
    <Card withBorder>
      <Stack gap="lg">
        <Group justify="space-between" align="baseline" wrap="wrap" gap="xs">
          <Group gap="md" align="center" wrap="wrap">
            <Title order={4}>Counterpart Relationship — {P} vs {C}</Title>
            {onPrimaryChange ? (
              <SegmentedControl
                size="xs"
                value={primary ?? "btc"}
                onChange={(v) => onPrimaryChange(v as "btc" | "eth")}
                data={[{ label: "BTC primary", value: "btc" }, { label: "ETH primary", value: "eth" }]}
              />
            ) : null}
          </Group>
          <Text size="xs" c="dimmed">Win = P/L &gt; 0 · direction = order vs counterpart</Text>
        </Group>

        {/* ---- Cohort 1: counterpart NOT traded ---- */}
        <Stack gap="xs">
          <Text size="sm" fw={700}>{P} traded, {C} counterpart NOT traded — how {C} closed vs the {P} trade</Text>
          {!hasNt ? (
            <Text size="sm" c="dimmed">No such trades in this range.</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Timeframe</Table.Th>
                  <Table.Th ta="right">{C} same dir (W/L)</Table.Th>
                  <Table.Th ta="right">{C} opposite (W/L)</Table.Th>
                  <Table.Th ta="right">{C} unresolved (W/L)</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr style={{ fontWeight: 700 }}>
                  <Table.Td>All</Table.Td>
                  <Table.Td ta="right" style={monoStyle}><WL {...nt.all.same} /></Table.Td>
                  <Table.Td ta="right" style={monoStyle}><WL {...nt.all.opposite} /></Table.Td>
                  <Table.Td ta="right" style={monoStyle}><WL {...nt.all.unresolved} /></Table.Td>
                  <Table.Td ta="right" style={monoStyle}>{ntTotal(nt.all)}</Table.Td>
                </Table.Tr>
                {nt.tfs.map((tf) => {
                  const g = nt.byTf.get(tf)!;
                  return (
                    <Table.Tr key={tf}>
                      <Table.Td style={monoStyle}>{tf}</Table.Td>
                      <Table.Td ta="right" style={monoStyle}><WL {...g.same} /></Table.Td>
                      <Table.Td ta="right" style={monoStyle}><WL {...g.opposite} /></Table.Td>
                      <Table.Td ta="right" style={monoStyle}><WL {...g.unresolved} /></Table.Td>
                      <Table.Td ta="right" style={monoStyle}>{ntTotal(g)}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>

        {/* ---- Cohort 2: both traded ---- */}
        <Stack gap="xs">
          <Text size="sm" fw={700}>{P} and {C} counterpart BOTH traded — direction match &amp; win/loss</Text>
          {!hasBt ? (
            <Text size="sm" c="dimmed">No such windows in this range.</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Timeframe</Table.Th>
                  <Table.Th>Direction</Table.Th>
                  <Table.Th ta="right">Both win</Table.Th>
                  <Table.Th ta="right">{P} win / {C} loss</Table.Th>
                  <Table.Th ta="right">{P} loss / {C} win</Table.Th>
                  <Table.Th ta="right">Both loss</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(["same", "different"] as const).map((dir) => (
                  <Table.Tr key={`all-${dir}`} style={{ fontWeight: 700 }}>
                    <Table.Td>{dir === "same" ? "All" : ""}</Table.Td>
                    <Table.Td>{dir === "same" ? "Same" : "Different"}</Table.Td>
                    <Table.Td ta="right" style={monoStyle} c="var(--mantine-color-profit-5)">{bt.all[dir].bw}</Table.Td>
                    <Table.Td ta="right" style={monoStyle}>{bt.all[dir].pwcl}</Table.Td>
                    <Table.Td ta="right" style={monoStyle}>{bt.all[dir].plcw}</Table.Td>
                    <Table.Td ta="right" style={monoStyle} c="var(--mantine-color-loss-5)">{bt.all[dir].bl}</Table.Td>
                    <Table.Td ta="right" style={monoStyle}>{btTotal(bt.all[dir])}</Table.Td>
                  </Table.Tr>
                ))}
                {bt.tfs.flatMap((tf) => {
                  const g = bt.byTf.get(tf)!;
                  return (["same", "different"] as const).map((dir) => (
                    <Table.Tr key={`${tf}-${dir}`}>
                      <Table.Td style={monoStyle}>{dir === "same" ? tf : ""}</Table.Td>
                      <Table.Td>{dir === "same" ? "Same" : "Different"}</Table.Td>
                      <Table.Td ta="right" style={monoStyle} c="var(--mantine-color-profit-5)">{g[dir].bw}</Table.Td>
                      <Table.Td ta="right" style={monoStyle}>{g[dir].pwcl}</Table.Td>
                      <Table.Td ta="right" style={monoStyle}>{g[dir].plcw}</Table.Td>
                      <Table.Td ta="right" style={monoStyle} c="var(--mantine-color-loss-5)">{g[dir].bl}</Table.Td>
                      <Table.Td ta="right" style={monoStyle}>{btTotal(g[dir])}</Table.Td>
                    </Table.Tr>
                  ));
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Paper withBorder radius="lg" p="md">
      <Stack gap={6}>
        <Text size="sm" c="dimmed" tt="uppercase" fw={700}>{label}</Text>
        <Text fw={800} size="2rem" style={monoStyle} c={color}>{value}</Text>
      </Stack>
    </Paper>
  );
}

/**
 * Per-market analytics route (`/analytics/current`). Resolves the active
 * market from the global `MarketProvider` and forwards it to the shared
 * dashboard body. The `MarketSwitcher` in the app shell remains the
 * source of truth for which market is displayed.
 */
export function AnalyticsDashboardPage() {
  const { marketId, market } = useMarket();
  const titleSuffix = market?.display_name ?? market?.short_name ?? marketId;
  return <AnalyticsDashboardBody marketId={marketId} titleSuffix={titleSuffix} />;
}
